"use client";

import { useQuery } from "@tanstack/react-query";
import { Clock3 } from "lucide-react";

import { EmptyState } from "@/components/ds/empty-state";
import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { getBillingOverview } from "@/lib/api/endpoints/billing";

function formatUsageValue(value: number, unit: string): string {
  if (unit === "req" && value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (Number.isInteger(value)) {
    return String(value);
  }
  return value.toFixed(1);
}

export function BillingPage() {
  const q = useQuery({
    queryKey: ["billing", "overview"],
    queryFn: getBillingOverview,
  });

  return (
    <EnterprisePageShell
      title="Billing & commissions"
    >
      {q.isLoading ? (
        <p className="p-4  text-caption text-muted">Loading billing…</p>
      ) : q.isError ? (
        <div className="p-4">
          <EmptyState
            icon={Clock3}
            title="Billing is coming soon"
            description="This module is not available yet. Usage-based billing and commission tracking will appear here once the feature is enabled."
          />
        </div>
      ) : !q.data?.usage.length ? (
        <p className="p-4 text-caption text-muted">No usage data.</p>
      ) : (
        <>
          <div className="grid gap-4 p-4 md:grid-cols-3">
            {q.data.usage.map((u) => (
              <div
                key={u.label}
                className="rounded-card border border-card-border bg-surface-primary px-4 py-4 shadow-raised"
              >
                <p className=" text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-muted">{u.label}</p>
                <p className=" text-lg font-semibold">
                  {formatUsageValue(u.value, u.unit)}{" "}
                  <span className="text-caption font-normal text-muted">{u.unit}</span>
                </p>
              </div>
            ))}
          </div>
          <div className="grid gap-6 px-4 pb-6 lg:grid-cols-2">
            <div className="overflow-hidden rounded-card border border-card-border bg-surface-primary shadow-raised">
              <div className="border-b border-card-border px-5 py-4">
                <h2 className=" text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted">Invoices</h2>
              </div>
              {!q.data.invoices.length ? (
                <p className="px-5 py-6 text-caption text-muted">No invoices.</p>
              ) : (
                <table className="w-full border-collapse  text-[0.8125rem]">
                  <thead className="bg-surface-secondary text-left text-micro uppercase tracking-[0.08em] text-muted">
                    <tr className="border-b border-card-border">
                      <th className="py-2 pr-2 font-medium">ID</th>
                      <th className="py-2 pr-2 font-medium">Period</th>
                      <th className="py-2 pr-2 font-medium">Amount</th>
                      <th className="py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.invoices.map((i) => (
                      <tr key={i.id} className="border-b border-card-border last:border-b-0">
                        <td className="px-5 py-3 pr-2">{i.id}</td>
                        <td className="px-5 py-3 pr-2 text-muted">{i.period}</td>
                        <td className="px-5 py-3 pr-2">{i.amount}</td>
                        <td className="px-5 py-3">{i.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="overflow-hidden rounded-card border border-card-border bg-surface-primary shadow-raised">
              <div className="border-b border-card-border px-5 py-4">
                <h2 className=" text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted">Commissions</h2>
              </div>
              <table className="w-full border-collapse  text-[0.8125rem]">
                  <thead className="bg-surface-secondary text-left text-micro uppercase tracking-[0.08em] text-muted">
                    <tr className="border-b border-card-border">
                    <th className="py-2 pr-2 font-medium">Org</th>
                    <th className="py-2 pr-2 font-medium">Period</th>
                    <th className="py-2 pr-2 font-medium">Rate</th>
                    <th className="py-2 font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.commissions.map((c) => (
                    <tr key={`${c.org}-${c.period}`} className="border-b border-[var(--table-border)] last:border-b-0">
                      <td className="px-5 py-3 pr-2">{c.org}</td>
                      <td className="px-5 py-3 pr-2 text-muted">{c.period}</td>
                      <td className="px-5 py-3 pr-2">{c.rate_percent}%</td>
                      <td className="px-5 py-3">{c.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </EnterprisePageShell>
  );
}
